import React, { useEffect, useState } from 'react';
import { Layout } from './components/common';
import type { NavigationTab, Machine } from './types';
import { getAllMachines, initDefaultData, getSetting } from './db';
import { syncQueueEngine } from './services/sync';
import {
  initSystemConfig,
  subscribeSystemDataUpdates,
} from './services/config/cloudConfigService';
import { InspectionView, DashboardView, SettingsView, AdminView } from './views';

export function App() {
  const [activeTab, setActiveTab] = useState<NavigationTab>('dashboard');
  const [machines, setMachines] = useState<Machine[]>([]);
  const [selectedMachineId, setSelectedMachineId] = useState<string>('CNC-01');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Load initial data and initialize embedded/cloud configuration
  const loadMachines = async () => {
    try {
      await initDefaultData();
      await initSystemConfig();
      const list = await getAllMachines();
      setMachines(list);
      if (list.length > 0 && !list.find((m) => m.id === selectedMachineId)) {
        setSelectedMachineId(list[0].id);
      }
    } catch (err) {
      console.error('[App] Error loading machines:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMachines();

    const handleOnline = () => {
      initSystemConfig().catch(() => {});
    };
    window.addEventListener('online', handleOnline);

    // Subscribe to centralized system updates (machines, templates, inspectors)
    const unsubscribe = subscribeSystemDataUpdates(() => {
      loadMachines();
    });

    return () => {
      window.removeEventListener('online', handleOnline);
      unsubscribe();
    };
  }, []);

  const selectedMachine = machines.find((m) => m.id === selectedMachineId) || machines[0];

  return (
    <Layout
      activeTab={activeTab}
      onSelectTab={setActiveTab}
      selectedMachineName={selectedMachine?.name}
      onManualSync={async () => {
        const gasUrl = (await getSetting<string>('gas_endpoint_url')) || '';
        const gasConfig = await getSetting<{ enabled?: boolean; webAppUrl?: string }>('gas_config');
        const activeUrl = gasConfig?.webAppUrl || gasUrl;

        if (!activeUrl) {
          alert('ยังไม่ได้กำหนด Google Apps Script Web App URL กรุณาไปที่หน้า Settings เพื่อเปิดใช้งานและวาง URL เชื่อมต่อ');
          setActiveTab('settings');
          return;
        }

        if (gasConfig && gasConfig.enabled === false) {
          alert('ระบบ Google Sheets Cloud Sync ถูกปิดใช้งานอยู่ กรุณาไปที่หน้า Settings เพื่อเปิดใช้งาน');
          setActiveTab('settings');
          return;
        }

        const res = await syncQueueEngine.drainQueue();
        await loadMachines();
        if (res.succeeded > 0) {
          alert(`ซิงค์ข้อมูลขึ้น Google Sheets สำเร็จ ${res.succeeded} รายการ`);
        }
      }}
    >
      {/* TAB 1: DASHBOARD */}
      {activeTab === 'dashboard' && (
        <DashboardView
          machines={machines}
          selectedMachineId={selectedMachineId}
          onSelectMachine={setSelectedMachineId}
          onStartInspection={(machineId) => {
            setSelectedMachineId(machineId);
            setActiveTab('inspection');
          }}
          onRefresh={loadMachines}
        />
      )}

      {/* TAB 2: INSPECTION */}
      {activeTab === 'inspection' && (
        <div className="animate-in fade-in duration-150">
          <InspectionView
            machines={machines}
            selectedMachineId={selectedMachineId}
            onSelectMachine={setSelectedMachineId}
            onInspectionSubmitted={async () => {
              await loadMachines();
            }}
          />
        </div>
      )}

      {/* TAB 3: ADMIN PANEL */}
      {activeTab === 'admin' && <AdminView />}

      {/* TAB 4: SETTINGS */}
      {activeTab === 'settings' && <SettingsView />}
    </Layout>
  );
}

